// src/Components/Common/LeaveNotifications.tsx
import React, { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Dropdown, DropdownToggle, DropdownMenu, Badge } from 'reactstrap';
import { useFaviconBadge } from './usefaviconbadge';

interface LeaveNotification {
    id: number;
    employee_id: number;
    employee_code?: string | null;
    employee_name?: string | null;
    leave_type_name?: string | null;
    leave_type_color?: string | null;
    start_date: string;
    end_date: string;
    number_of_days: number;
    reason: string;
    status: number;
    requested_at: string;
}

type ConnState = 'connecting' | 'connected' | 'disconnected';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000/api';

const ADMIN_LEAVE_LIST = `${API_URL}/leave`;
const ADMIN_LEAVE_EVENTS = `${API_URL}/leave/events`;

// ── connection indicator colours ──
const CONN_COLOR: Record<ConnState, string> = {
    connecting: '#F59E0B', // amber
    connected: '#10B981',  // green
    disconnected: '#EF4444', // red
};

const CONN_TITLE: Record<ConnState, string> = {
    connecting: 'Connecting to live updates…',
    connected: 'Live',
    disconnected: 'Not connected — check your connection or login again',
};

const formatDate = (iso: string): string => {
    try {
        return new Date(iso).toLocaleDateString('en-IN', {
            day: '2-digit',
            month: 'short',
            year: 'numeric',
        });
    } catch {
        return iso;
    }
};

const formatRelative = (iso: string): string => {
    try {
        const diff = Math.floor((Date.now() - new Date(iso).getTime()) / 1000);
        if (diff < 60) return 'just now';
        if (diff < 3600) return `${Math.floor(diff / 60)}m ago`;
        if (diff < 86400) return `${Math.floor(diff / 3600)}h ago`;
        return `${Math.floor(diff / 86400)}d ago`;
    } catch {
        return '';
    }
};

const mapRow = (r: any): LeaveNotification => ({
    id: r.id,
    employee_id: r.employee_id,
    employee_code: r.employee?.employee_code ?? r.employee_code ?? null,
    employee_name: r.employee?.name ?? null,
    leave_type_name: r.leaveType?.name ?? null,
    leave_type_color: r.leaveType?.color_hex ?? null,
    start_date: r.start_date,
    end_date: r.end_date,
    number_of_days: Number(r.number_of_days),
    reason: r.reason,
    status: r.status,
    requested_at: r.requested_at ?? new Date().toISOString(),
});

const LeaveNotifications: React.FC = () => {
    const navigate = useNavigate();

    const [open, setOpen] = useState(false);
    const [pending, setPending] = useState<LeaveNotification[]>([]);
    const [conn, setConn] = useState<ConnState>('connecting');

    const esRef = useRef<EventSource | null>(null);

    // ── Initial load — admin endpoint
    useEffect(() => {
        let alive = true;
        const token = localStorage.getItem('accessToken');

        fetch(`${ADMIN_LEAVE_LIST}?status=0`, {
            headers: {
                Authorization: `Bearer ${token}`,
                Accept: 'application/json',
            },
            credentials: 'include',
        })
            .then((res) => (res.ok ? res.json() : null))
            .then((json) => {
                if (!alive || !json) return;
                const rows = (json?.data?.requests ?? json?.data?.leaves ?? []) as any[];
                setPending(
                    rows
                        .map(mapRow)
                        .sort(
                            (a, b) =>
                                new Date(b.requested_at).getTime() -
                                new Date(a.requested_at).getTime(),
                        ),
                );
            })
            .catch(() => { });

        return () => {
            alive = false;
        };
    }, []);

    // ── SSE — admin endpoint
    useEffect(() => {
        const token = localStorage.getItem('accessToken');
        if (!token) {
            setConn('disconnected');
            return;
        }

        const url = `${ADMIN_LEAVE_EVENTS}?token=${encodeURIComponent(token)}`;

        setConn('connecting');
        const es = new EventSource(url, { withCredentials: true });
        esRef.current = es;

        // Treat any message on the stream as a sign we're alive. The server
        // sends `event: connected` once at open, and `: ping` heartbeats
        // every 25 s. `onopen` fires when the HTTP response starts, which is
        // the best signal we have in the browser.
        es.onopen = () => {
            setConn('connected');
        };

        es.addEventListener('connected', () => setConn('connected'));

        es.addEventListener('leave', (e: MessageEvent) => {
            // Any successful event means the stream is healthy
            setConn('connected');

            try {
                const { type, payload } = JSON.parse(e.data);
                const leave = payload?.leave;
                if (!leave?.id) return;

                setPending((prev) => {
                    if (type === 'LEAVE_CANCELLED') {
                        return prev.filter((n) => n.id !== leave.id);
                    }
                    if (leave.status !== 0) {
                        return prev.filter((n) => n.id !== leave.id);
                    }
                    const incoming = mapRow(leave);
                    const others = prev.filter((n) => n.id !== leave.id);
                    return [incoming, ...others].sort(
                        (a, b) =>
                            new Date(b.requested_at).getTime() -
                            new Date(a.requested_at).getTime(),
                    );
                });
            } catch (err) {
                console.warn('Failed to parse leave event:', err);
            }
        });

        es.onerror = () => {
            // Browser will keep retrying. Mark as disconnected so the red dot
            // appears. If the server comes back, onopen fires again and the
            // dot flips back to green.
            setConn('disconnected');
        };

        return () => {
            es.close();
            esRef.current = null;
        };
    }, []);

    const handleClick = (n: LeaveNotification) => {
        setOpen(false);
        navigate(`/leaverequest?focus=${n.id}`);
    };

    const unreadCount = pending.length;
    useFaviconBadge(unreadCount);
    return (
        <Dropdown
            isOpen={open}
            toggle={() => setOpen((v) => !v)}
            className="topbar-head-dropdown header-item"
            direction="down"
        >
            <DropdownToggle
                tag="button"
                type="button"
                className="btn btn-icon btn-topbar btn-ghost-secondary rounded-circle position-relative"
                aria-label="Pending leave requests"
            >
                <i className="bx bx-bell fs-22" />
                {unreadCount > 0 && (
                    <Badge
                        color="danger"
                        pill
                        className="position-absolute"
                        style={{
                            top: '2px',
                            right: '2px',
                            fontSize: '10px',
                            minWidth: '18px',
                            padding: '3px 5px',
                            lineHeight: 1,
                        }}
                    >
                        {unreadCount > 99 ? '99+' : unreadCount}
                    </Badge>
                )}

                {/* Connection indicator — green live, amber connecting, red offline */}
                <span
                    title={CONN_TITLE[conn]}
                    style={{
                        position: 'absolute',
                        bottom: '4px',
                        right: '6px',
                        width: '8px',
                        height: '8px',
                        borderRadius: '50%',
                        backgroundColor: CONN_COLOR[conn],
                        boxShadow:
                            conn === 'connected'
                                ? '0 0 0 2px rgba(16,185,129,0.25)'
                                : conn === 'connecting'
                                    ? '0 0 0 2px rgba(245,158,11,0.25)'
                                    : '0 0 0 2px rgba(239,68,68,0.25)',
                        transition: 'background-color .2s ease',
                    }}
                />
            </DropdownToggle>

            <DropdownMenu
                className="dropdown-menu-end p-0"
                style={{ width: '380px', maxWidth: '92vw' }}
            >
                <div
                    className="px-3 py-2 border-bottom d-flex justify-content-between align-items-center"
                    style={{ fontSize: '0.9rem' }}
                >
                    <span className="fw-semibold">Pending Leave Requests</span>
                    <span
                        className="small d-flex align-items-center gap-1"
                        style={{ color: CONN_COLOR[conn], fontSize: '0.72rem' }}
                        title={CONN_TITLE[conn]}
                    >
                        <span
                            style={{
                                width: '6px',
                                height: '6px',
                                borderRadius: '50%',
                                backgroundColor: CONN_COLOR[conn],
                                display: 'inline-block',
                            }}
                        />
                        {conn === 'connected'
                            ? 'Live'
                            : conn === 'connecting'
                                ? 'Connecting…'
                                : 'Offline'}
                    </span>
                </div>

                <div style={{ maxHeight: '420px', overflowY: 'auto' }}>
                    {pending.length === 0 ? (
                        <div className="text-center text-muted py-4 small">
                            <i className="bx bx-bell-off fs-24 d-block mb-1 opacity-50" />
                            No pending leave requests
                        </div>
                    ) : (
                        pending.map((n) => (
                            <button
                                key={n.id}
                                type="button"
                                onClick={() => handleClick(n)}
                                className="w-100 text-start border-0 bg-transparent px-3 py-2 border-bottom"
                                style={{ cursor: 'pointer' }}
                            >
                                <div className="d-flex align-items-start gap-2">
                                    <span
                                        style={{
                                            display: 'inline-block',
                                            width: '10px',
                                            height: '10px',
                                            borderRadius: '50%',
                                            marginTop: '6px',
                                            flexShrink: 0,
                                            backgroundColor: n.leave_type_color || '#6c757d',
                                        }}
                                    />
                                    <div className="flex-grow-1">
                                        <div className="d-flex justify-content-between align-items-start">
                                            <div
                                                className="fw-semibold text-truncate"
                                                style={{ fontSize: '0.85rem', maxWidth: '240px' }}
                                            >
                                                {n.employee_name || `Employee #${n.employee_id}`}
                                                {n.employee_code && (
                                                    <span className="text-muted ms-1" style={{ fontSize: '0.7rem' }}>
                                                        ({n.employee_code})
                                                    </span>
                                                )}
                                            </div>
                                            <span
                                                className="text-muted ms-2"
                                                style={{ fontSize: '0.65rem', whiteSpace: 'nowrap' }}
                                            >
                                                {formatRelative(n.requested_at)}
                                            </span>
                                        </div>
                                        <div className="text-muted" style={{ fontSize: '0.75rem' }}>
                                            {n.leave_type_name || 'Leave'} · {n.number_of_days} day
                                            {n.number_of_days === 1 ? '' : 's'}
                                        </div>
                                        <div className="text-muted" style={{ fontSize: '0.75rem' }}>
                                            {formatDate(n.start_date)} → {formatDate(n.end_date)}
                                        </div>
                                        {n.reason && (
                                            <div
                                                className="text-truncate mt-1"
                                                style={{ fontSize: '0.72rem', color: '#555', maxWidth: '280px' }}
                                                title={n.reason}
                                            >
                                                {n.reason}
                                            </div>
                                        )}
                                    </div>
                                </div>
                            </button>
                        ))
                    )}
                </div>

                {pending.length > 0 && (
                    <div className="border-top text-center">
                        <button
                            className="btn btn-link btn-sm w-100 text-decoration-none"
                            style={{ fontSize: '0.8rem' }}
                            onClick={() => {
                                setOpen(false);
                                navigate('/leave-requests');
                            }}
                        >
                            View all leave requests
                        </button>
                    </div>
                )}
            </DropdownMenu>
        </Dropdown>
    );
};

export default LeaveNotifications;